import React, { useMemo, useState, useEffect } from 'react'
import { DrawerPopup } from 'components/UI/DrawerPopup'
import { Box, Button, Checkbox, Divider, FormControlLabel, Stack, Typography } from '@mui/material'
import { Share2 } from 'lucide-react'
import { useDispatch } from 'store/hooks'
import { changeCertificateStatusByCustomer } from 'store/certificates/actions'
import { SuccessState } from 'components/UI/SuccessState'
import { border, font } from 'settings/tokens';
import {
  CertificateDisplayStatusBorderColor,
  CertificateDisplayStatusFillColor,
  CertificateDisplayStatusTextColor,
} from 'utils/displayStatusColorMapper';

/**
 * The two states this drawer can set, drawn as the SAME chips the certificate
 * shows afterwards — colours come from the shared status ramps rather than being
 * re-picked here, so the chip you choose is the chip you get.
 */
const MISSING_OPTIONS = (['lost', 'stolen'] as const).map(value => ({
  value,
  label: value === 'lost' ? 'Lost' : 'Stolen',
  fillColor: CertificateDisplayStatusFillColor[value],
  borderColor: CertificateDisplayStatusBorderColor[value],
  textColor: CertificateDisplayStatusTextColor[value],
}));

/** Notification channels, so the two rows cannot drift apart. */
const NOTIFY_CHANNELS = [
  { key: 'text' as const, label: 'Text' },
  { key: 'email' as const, label: 'Email' },
];

type ReportMissingDrawerProps = {
  open: boolean
  onClose: () => void
  certificateId?: string
  /**
   * Report SEVERAL at once — the certificates list's bulk bar passes the whole
   * selection here. Takes precedence over `certificateId` when both are given;
   * the single-record callers pass only `certificateId` and are unchanged.
   */
  certificateIds?: string[]
  onViewCertificate?: () => void
  onShare?: () => void
  onStatusChange?: () => void
}

export const ReportMissingDrawer: React.FC<ReportMissingDrawerProps> = ({ open, onClose, certificateId, certificateIds, onViewCertificate, onShare, onStatusChange }) => {
  const dispatch = useDispatch()
  const [status, setStatus] = useState<'lost' | 'stolen' | ''>('')
  const [notifyText, setNotifyText] = useState<boolean>(true)
  const [notifyEmail, setNotifyEmail] = useState<boolean>(true)
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false)
  /** How many of `targetIds` have come back so far — drives "Reporting 2 of 5…". */
  const [doneCount, setDoneCount] = useState<number>(0)
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  /**
   * The ids a retry should run, once a run has left some behind.
   *
   * `null` until something fails, so the first press always targets the full
   * selection. After a partial failure it holds ONLY the ids that failed — the
   * ones that succeeded are already reported and re-sending them would be a
   * second write for no reason.
   */
  const [retryIds, setRetryIds] = useState<string[] | null>(null)
  const [errorText, setErrorText] = useState<string>('')
  /**
   * How many records the completed run covered, SNAPSHOT at success.
   *
   * The success screen must not derive its count from `certificateIds` live. Two
   * reasons, one of which is a caller's business and one of which is not:
   *
   *  - The obvious way to wire a bulk caller is to clear its selection in
   *    `onStatusChange` — which fires as this screen mounts, so the prop would be
   *    empty by the time the screen rendered and a five-record report would
   *    announce itself as a single one. The certificates bulk bar deliberately
   *    clears on CLOSE instead, but this component should not be relying on every
   *    future caller getting that subtlety right.
   *  - After a partial failure the retry run covers only the remainder, so
   *    neither the last run's length nor the failure count is the number the user
   *    should be shown. The snapshot is taken from the whole selection.
   */
  const [submittedCount, setSubmittedCount] = useState<number>(0)

  /**
   * ONE form, applied to N records.
   *
   * `certificateIds` wins when present so a bulk caller cannot half-configure
   * this by passing both; the single-record pages keep passing `certificateId`
   * and land on a one-element array, which is the same code path.
   */
  const targetIds = useMemo(
    () => (certificateIds?.length ? certificateIds : certificateId ? [certificateId] : []),
    [certificateIds, certificateId],
  )
  const isBulk = targetIds.length > 1

  const isReportDisabled = useMemo(
    () => status === '' || isSubmitting || targetIds.length === 0,
    [status, isSubmitting, targetIds.length],
  )

  useEffect(() => {
    if (!open) {
      setIsSubmitted(false)
      setStatus('')
      setNotifyText(true)
      setNotifyEmail(true)
      setDoneCount(0)
      setIsSubmitting(false)
      setRetryIds(null)
      setErrorText('')
      setSubmittedCount(0)
    }
  }, [open])

  /*
   * ONE record's status change, as a promise.
   *
   * ⚠️ CALLERS MUST AWAIT THIS BEFORE STARTING THE NEXT ONE. The saga behind
   * `CHANGE_CERTIFICATE_STATUS_BY_CUSTOMER` is a `takeLatest`
   * (`store/certificates/saga.tsx`), so a second dispatch while the first is in
   * flight CANCELS the first — its request never completes and its callback never
   * fires. Firing N of these in a `Promise.all` would therefore report exactly
   * ONE certificate, silently drop the rest, and hang on N-1 promises that can
   * never resolve. The sequential loop below is not a politeness; it is the only
   * shape this endpoint supports from the client.
   */
  const reportOne = (id: string) =>
    new Promise<boolean>(resolve => {
      dispatch(
        changeCertificateStatusByCustomer(
          { certificateId: id, status: status as 'lost' | 'stolen' },
          (res: any) => resolve(Boolean(res?.success)),
        ),
      )
    })

  /**
   * Apply the form to every selected certificate, one at a time.
   *
   * ## Why a client-side fan-out and not a batch call
   *
   * There is no batch endpoint: `PATCH /certificates/customer/status/:certificateId`
   * is the only route, and inventing one was out of scope. A loop over a
   * single-record endpoint is not atomic, and that is stated rather than hidden —
   * see the partial-failure branch, which reports the real numbers instead of a
   * blanket "submitted".
   *
   * It is nevertheless HONEST for this particular action in a way it would not be
   * for, say, a payment: lost/stolen is an independent per-record flag with no
   * cross-record invariant, so "three of five are now lost" is a coherent state,
   * not a corrupt one. The retry then targets only the two that failed.
   *
   * ## Closing mid-run does not abort
   *
   * The loop keeps going if the drawer is dismissed while it is working. That is
   * deliberate: the user pressed Report, the writes were requested, and stopping
   * half way would leave an arbitrary prefix of the selection reported with
   * nothing on screen saying which. React 18 makes the orphaned `setState` calls
   * no-ops, so nothing warns and nothing leaks.
   */
  const handleReport = async () => {
    if (!status) return
    const ids = retryIds ?? targetIds
    if (!ids.length) return

    setIsSubmitting(true)
    setErrorText('')
    setDoneCount(0)

    const failed: string[] = []
    for (const id of ids) {
      const ok = await reportOne(id)
      if (!ok) failed.push(id)
      setDoneCount(n => n + 1)
    }

    setIsSubmitting(false)

    if (failed.length) {
      setRetryIds(failed)
      setErrorText(
        failed.length === ids.length
          ? `Couldn't submit ${ids.length === 1 ? 'the report' : 'the reports'}. Try again.`
          : `${ids.length - failed.length} of ${ids.length} reported. Try again for the ${failed.length === 1 ? 'other one' : `other ${failed.length}`}.`,
      )
      // The ones that DID land have changed the list behind this drawer, so the
      // caller is told even though the run as a whole failed. Skipping this on a
      // partial failure would leave the list showing them as active.
      if (failed.length < ids.length) onStatusChange?.()
      return
    }

    setRetryIds(null)
    // `targetIds`, not `ids` — after a partial failure `ids` is only the retried
    // remainder, and the screen is announcing the whole run, not the last leg.
    setSubmittedCount(targetIds.length)
    setIsSubmitted(true)
    onStatusChange?.()
  }

  /*
   * Which follow-up actions the success screen can honestly offer.
   *
   * "View certificate" and "Share" name ONE certificate, so they are meaningless
   * for a bulk report — there is no single record to view, and picking one for
   * the user would be a guess. They are also gated on the handler existing at
   * all: a caller that passes no `onShare` used to get a Share button that ran
   * `onShare?.()` and did nothing, which is the same class of defect as the bulk
   * bar's old no-op Transfer.
   */
  const wasBulk = submittedCount > 1
  const canViewCertificate = !wasBulk && Boolean(onViewCertificate)
  const canShare = !wasBulk && Boolean(onShare)

  const successContent = (
    <Stack sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* VERA-315: the shared badge. This drew its check as the TEXT CHARACTER
          "✓" at 40px bold in an 80px circle under a 20px/600 title, while the
          gift/scan success used an SVG check in an 88px circle under a 16px/500
          title — the same moment in two flows with two badges and two type
          scales. `SuccessState` owns the circle, its faint edge, the
          spinner-then-draw and the type. */}
      <Stack sx={{ flex: 1, justifyContent: 'center', py: 4 }}>
        <SuccessState
          title={wasBulk ? `${submittedCount} reports submitted` : 'Report submitted'}
          description={
            wasBulk
              ? // The single-record copy is about sharing THIS certificate's
                // scanning history with law enforcement, which is a per-record
                // errand — the bulk screen has no certificate to point at, so it
                // says what actually happened and sends them to the records.
                'Each certificate is now flagged. Open one from the list to share its scanning history with law enforcement.'
              : 'You can share the certificate and scanning history with law enforcement for further investigation, by creating a custom link or using an existing one.'
          }
        />
      </Stack>

      {/* 12px between the pair, not 16 — §29's button-group gap, which is
          `spacing={1.5}` on this app's 8px MUI base (NOT the 4px @repo/ui one).
          16 read as two unrelated buttons rather than one decision. */}
      <Stack spacing={1.5} sx={{ width: '100%', pt: 3 }}>
        {canViewCertificate && (
          <Button
            variant="contained"
            fullWidth
            sx={{
              height: 44,
              borderRadius: '16px',
              textTransform: 'none',
              backgroundColor: theme => theme.palette.common.black,
              color: theme => theme.palette.common.white,
              '&:hover': {
                backgroundColor: theme => theme.palette.grey[800],
              },
            }}
            onClick={() => {
              onClose()
              onViewCertificate?.()
            }}
          >
            View certificate
          </Button>
        )}

        {canShare && (
          <Button
            variant="outlined"
            fullWidth
            startIcon={<Share2 size={18} />}
            sx={{
              height: 44,
              borderRadius: '16px',
              textTransform: 'none',
              borderColor: theme => theme.palette.grey[300],
              color: theme => theme.palette.text.primary,
              backgroundColor: theme => theme.palette.common.white,
              '&:hover': {
                backgroundColor: theme => theme.palette.grey[50],
              },
            }}
            onClick={() => {
              onShare?.()
            }}
          >
            Share
          </Button>
        )}

        {/* A success screen with no way off it is a dead end — the sheet's header
            X is the only exit and it does not read as "I am finished". Rendered
            only when neither per-record action is available, so the single-record
            screen keeps its existing pair and gains nothing. */}
        {!canViewCertificate && !canShare && (
          <Button
            variant="contained"
            fullWidth
            sx={{
              height: 44,
              borderRadius: '16px',
              textTransform: 'none',
              backgroundColor: theme => theme.palette.common.black,
              color: theme => theme.palette.common.white,
              '&:hover': {
                backgroundColor: theme => theme.palette.grey[800],
              },
            }}
            onClick={onClose}
          >
            Done
          </Button>
        )}
      </Stack>
    </Stack>
  )

  const formContent = (
    <Stack spacing={2} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Stack spacing={2} sx={{ flex: 1 }}>
        {/* The count is named up front on a bulk report, because this form has no
            other way to say what it is about to act on: the single-record callers
            open it FROM the record, so "your item" is unambiguous there, while
            from the list the same sentence would leave the user guessing whether
            it means the selection or the row they last touched. Naming the number
            is also the last checkpoint before a five-record write. */}
        <Typography sx={{ fontSize: 14 }} color="text.secondary">
          {isBulk
            ? `Let us know if these ${targetIds.length} items are lost or stolen. They'll all be given the same status, and we'll notify you with updates if any of them is scanned.`
            : "Let us know if your item is lost or stolen. We'll notify you with updates if your item is scanned."}
        </Typography>

        {/* VERA-315: status CHIPS, not radios — and agreed, it is cleaner.
            Lost and Stolen are not arbitrary options, they are two of the
            certificate's own statuses, and the app already draws them as chips
            everywhere else (the title row, the certificates list, the filter).
            Picking the state you are about to apply, in the exact treatment it
            will appear in afterwards, is a preview of the outcome rather than a
            form control describing it — and the chip's colour carries the
            severity difference between lost and stolen, which two identical
            grey radios could not.

            Still a RADIO GROUP semantically: `role='radio'` + `aria-checked`,
            arrow-key and Space handling, one selection at a time. The chips are
            single-select — this is a choice between two states, not a filter. */}
        <Stack
          direction='row'
          role='radiogroup'
          aria-label='Missing status'
          sx={{ gap: 1, flexWrap: 'wrap' }}
        >
          {MISSING_OPTIONS.map(opt => {
            const isOn = status === opt.value
            return (
              <Box
                key={opt.value}
                role='radio'
                aria-checked={isOn}
                tabIndex={0}
                onClick={() => setStatus(opt.value as any)}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    setStatus(opt.value as any)
                  }
                }}
                sx={{
                  cursor: 'pointer',
                  borderRadius: '999px',
                  px: '14px',
                  py: '8px',
                  minHeight: 36,
                  display: 'flex',
                  alignItems: 'center',
                  // Weight-neutral: 1px border and identical padding in both
                  // states, so choosing never shifts the row.
                  border: `1px solid ${isOn ? opt.borderColor : border.subtle} !important`,
                  backgroundColor: isOn ? opt.fillColor : 'transparent',
                  color: isOn ? opt.textColor : 'text.primary',
                  transition: 'background-color .12s, border-color .12s, color .12s',
                  '&:focus-visible': {
                    outline: theme => `2px solid ${theme.palette.text.primary}`,
                    outlineOffset: '-2px',
                  },
                }}
              >
                <Typography sx={{ fontSize: font.label, fontWeight: 500, color: 'inherit' }}>
                  {opt.label}
                </Typography>
              </Box>
            )
          })}
        </Stack>

        <Box>
          <Typography sx={{ fontSize: font.label, fontWeight: 500, mb: 1.5 }}>
            Receive updates via:
          </Typography>
          {/* VERA-315: the two rows were flush against each other and inset from
              nothing — MUI's FormControlLabel carries a -11px left margin to let
              a checkbox's ripple overhang, so these sat further LEFT than the
              chips above them and read as a denser, differently-aligned block.

              `ml: 0` puts them back on the same left edge as everything else in
              the sheet, and 12px between rows gives two tap targets room to be
              distinguished (§29 — 12px is spacing 1.5 on this app's 8px base). */}
          <Stack spacing={1.5}>
            {NOTIFY_CHANNELS.map(ch => (
              <FormControlLabel
                key={ch.key}
                sx={{ color: 'text.primary', ml: 0, gap: 1 }}
                control={
                  <Checkbox
                    checked={ch.key === 'text' ? notifyText : notifyEmail}
                    onChange={(_, c) => (ch.key === 'text' ? setNotifyText(c) : setNotifyEmail(c))}
                    // No colour override: `settings/mui/MuiCheckbox.ts` now paints
                    // every checkbox `text.primary` in both states. This site used
                    // to set exactly that by hand, working around a base default of
                    // `primary.contrastText` that rendered white on white.
                    sx={{ p: 0 }}
                  />
                }
                label={<Typography sx={{ fontSize: font.label }}>{ch.label}</Typography>}
              />
            ))}
          </Stack>
        </Box>
      </Stack>

      <Divider sx={{ borderColor: theme => theme.palette.grey[300], my: 1 }} />

      {/* Above the button, not a toast: this is the state of the thing the button
          is about to re-run, so it belongs where the button is. It also has to
          survive the drawer being scrolled, which a toast over a bottom sheet on a
          375px phone does not reliably do. */}
      {errorText && (
        <Typography sx={{ fontSize: font.label, lineHeight: 1.4 }} color="error.main">
          {errorText}
        </Typography>
      )}

      <Button
        variant="contained"
        fullWidth
        disabled={isReportDisabled}
        sx={{
          height: 44,
          borderRadius: '16px',
          textTransform: 'none',
          backgroundColor: theme => theme.palette.primary.main,
          color: theme => theme.palette.primary.contrastText,
          '&:hover': {
            backgroundColor: theme => theme.palette.primary.dark,
          },
          // VERA-304: see GiftCertificate.tsx — the theme owns the disabled
          // primary. A local `&:disabled` ties on specificity with the theme's
          // `.Mui-disabled` and wins on source order, so it must not exist here.
        }}
        onClick={handleReport}
      >
        {/* The label carries the progress, rather than a spinner beside a static
            word: a sequential fan-out over five records is several seconds long,
            and "Reporting 2 of 5…" is the only thing on screen that distinguishes
            "working" from "stuck". A single-record report resolves fast enough
            that a counter would only flash, so it keeps the plain verb. */}
        {isSubmitting
          ? isBulk
            ? `Reporting ${Math.min(doneCount + 1, (retryIds ?? targetIds).length)} of ${(retryIds ?? targetIds).length}…`
            : 'Reporting…'
          : retryIds
            ? 'Try again'
            : 'Report'}
      </Button>
    </Stack>
  )

  const content = isSubmitted ? successContent : formContent

  return (
    <DrawerPopup
      title="Report missing"
      child={content}
      handleOpen={open}
      handleClose={onClose}
    />
  )
}


